import {inject, Injectable} from '@angular/core';

import * as APIM from '@common/utils/dist/models/api-messages.js';
import {UtilsPL2 as U} from '@common/utils/dist/utils/utils-pl2.js';

import {Store} from '@stores/abstract-store';

import {AIProgressMessage} from '@components/ai-progress/ai-generation-message/ai-generation-message.component';
import {TranslateService} from '@ngx-translate/core';

/**
 * List of non-ai-generated message ids;
 * NOTE: It also describes the order of the messages in the store.
 */
export enum BuiltinMessageIds {
  Initial = -4,
  Status = -3,
  LivePreview = -2,
}

export interface AIProgressMessageState {
  [trackingId: string]: AIProgressMessage[];
}

@Injectable({providedIn: 'root'})
export class AIProgressMessageStore extends Store<AIProgressMessageState> {
  private _translateService = inject(TranslateService);

  constructor() {
    super({});
  }

  upsert(trackingId: string, message: AIProgressMessage) {
    const current = this.state()[trackingId] || [];
    const existingIndex = current.findIndex((m) => m.id === message.id);

    if (existingIndex >= 0) {
      this.setState({
        ...this.state(),
        [trackingId]: current.map((m, i) =>
          i === existingIndex ? message : m,
        ),
      });

      return;
    }

    this.setState({
      ...this.state(),
      [trackingId]: this._putInPosition(current, message),
    });
  }

  remove(trackingId: string, id: number) {
    const current = this.state()[trackingId] || [];
    this.setState({
      ...this.state(),
      [trackingId]: current.filter((m) => m.id !== id),
    });
  }

  clear(trackingId: string) {
    this.setState({
      ...this.state(),
      [trackingId]: [],
    });
  }

  ingestChunk(chunk: APIM.StreamingChunk) {
    if (U.isEmpty(chunk.messages)) {
      console.log('No messages found in chunk');
      console.log(chunk);
      return;
    }

    const existingMessages = this.state()[chunk.trackingId] || [];
    const allComplete = chunk.messages.every((msg) => {
      const existing = existingMessages.find((m) => m.id === msg.id);
      return existing?.isComplete === true;
    });

    // we should skip this message
    // because it is an incomplete message
    // that came after a complete message
    // which means it's a result of a race condition
    if (allComplete) {
      return;
    }

    for (const msg of chunk.messages) {
      this.upsert(
        chunk.trackingId,
        this._streamingMessageToAIProgressMessage(chunk, msg),
      );
    }
  }

  private _streamingMessageToAIProgressMessage(
    chunk: APIM.StreamingChunk,
    msg: APIM.StreamingMessage,
  ): AIProgressMessage {
    if (msg.type === 'group') {
      return {
        id: msg.id,
        title:
          msg.title ??
          this._translateService.instant(
            'ai-progress-message-store.groupedMessageTitle',
          ),
        content: msg.content.map((c) =>
          this._streamingMessageToAIProgressMessage(chunk, c),
        ),
        type: 'group',
        ord: chunk.ord,
        isComplete: chunk.isComplete,
      };
    }

    return {
      ...msg,
      ord: chunk.ord,
      isComplete: chunk.isComplete ?? false,
      status: 'streaming',
    };
  }

  private _putInPosition(
    current: AIProgressMessage[],
    message: AIProgressMessage,
  ): AIProgressMessage[] {
    if (current.length === 0) {
      return [message];
    }

    const greaterThanCurrentOrdIndex = current.findIndex(
      (m) => m.ord > message.ord,
    );

    if (greaterThanCurrentOrdIndex >= 0) {
      const head = current.slice(0, greaterThanCurrentOrdIndex);
      const tail = current.slice(greaterThanCurrentOrdIndex);

      return [...head, message, ...tail];
    }

    return [...current, message];
  }
}
